import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import Auth from "src/Auth";
import Cookies from "universal-cookie";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { PartnerCodeBrandingData } from "~shared/types/partner-code-types";
import { createSafeStorage } from "./safeStorage";

type PartnerCodeState = {
  partnerCode: string | null;
  partnerName: string | null;
  brandingInfo: PartnerCodeBrandingData | null;
  setPartnerCode: (partnerCode: string | null) => void;
  setPartnerName: (partnerName: string | null) => void;
  setBrandingInfo: (brandingInfo: PartnerCodeBrandingData | null) => void;
  reset: () => void;
};

const initialState = {
  partnerCode: null,
  partnerName: null,
  brandingInfo: null,
};

function readLocalStorageItem(key: string): string | null {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "partner_code_store",
      level: "warning",
      message: "Unable to read partner code localStorage",
      data: { key, message: error instanceof Error ? error.message : String(error) },
    });
    return null;
  }
}

export const usePartnerCodeStore = create<PartnerCodeState>()(
  persist(
    (set) => ({
      ...initialState,
      setPartnerCode: (partnerCode) => set({ partnerCode }),
      setPartnerName: (partnerName) => set({ partnerName }),
      setBrandingInfo: (brandingInfo) => set({ brandingInfo }),
      reset: () => set(initialState),
    }),
    {
      name: "partner-code-store",
      storage: createJSONStorage(() => createSafeStorage(typeof window !== "undefined" ? window.localStorage : null)),
    },
  ),
);

export const WithPartnerCodeStore = ({ children }: { children: React.ReactNode }) => {
  const { setPartnerCode, setPartnerName, setBrandingInfo } = usePartnerCodeStore();

  const partnerCode = useQuery({
    queryKey: ["partnerCode"],
    queryFn: async () => {
      const cookies = new Cookies();
      const cookiePartnerCode = cookies.get("partner_code");
      return (typeof cookiePartnerCode === "string" ? cookiePartnerCode : null) || readLocalStorageItem("partner_code");
    },
    onSuccess: (data) => {
      setPartnerCode(data);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    refetchOnWindowFocus: true,
  });

  useQuery({
    queryKey: ["partnerName"],
    queryFn: async () => {
      return readLocalStorageItem("partner_name");
    },
    onSuccess: (data) => {
      setPartnerName(data);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    refetchOnWindowFocus: true,
  });

  useQuery({
    queryKey: ["brandingInfo", partnerCode.data],
    queryFn: async () => {
      if (!partnerCode.data) return null;

      const response = await Auth.fetchWithIdToken(`/api/partners/get-branding`, {
        partnerCode: partnerCode.data,
      });

      if (!response?.success || !response.brandingInfo) {
        Sentry.addBreadcrumb({
          category: "partner_code_store",
          level: "info",
          message: "Partner branding response omitted branding info",
          data: { partnerCode: partnerCode.data, success: response?.success },
        });
        return null;
      }

      return response.brandingInfo as PartnerCodeBrandingData;
    },
    enabled: !!partnerCode.data,
    onSuccess: (data) => {
      setBrandingInfo(data);
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    refetchOnWindowFocus: true,
  });

  return children;
};
